import { Controller } from "@hotwired/stimulus"

/**
 * Connects to data-controller="split-panel"
 *
 * Enables a draggable horizontal split layout between a left and right panel.
 * The user can resize the left panel by dragging the handle, and the width is saved to `localStorage` to persist between page loads.
 *
 * Required HTML:
 * <div
 *    data-controller="split-panel"
 *    data-split-id="unique-id"          <!-- optional but recommended -->
 * >
 *    <div data-split-panel-target="left"></div>
 *    <div data-split-panel-target="handle"></div>
 *    <div data-split-panel-target="right"></div>
 * </div>
 *
 * LocalStorage:
 * - Saves the left panel width under a unique key:`split-left-width-{data-split-id}` or `split-left-width-{element.id}`.
 * - If neither ID nor data-split-id is provided, the index of the component on the page is used (e.g., `split-left-width-0`).
 */
export default class extends Controller {
  static targets = ["handle", "left", "right"]

  connect() {
    // Initialize state
    this.#isDragging = false
    this.#startX = 0
    this.#startWidth = 0

    // Load and cache CSS variables
    const root = getComputedStyle(document.documentElement)
    this.#handleWidth = parseInt(root.getPropertyValue("--split-handle-width"), 10) || 0
    this.#minRightWidth = this.#parseRemToPx(root.getPropertyValue("--split-min-right"))

    // Cache left panel margin-right for handle positioning
    const leftStyle = getComputedStyle(this.leftTarget)
    this.#leftMarginRight = parseFloat(leftStyle.marginRight) || 0

    this.#mainElement = this.element.closest(".page")
    this.#storageKey = this.#getStorageKey()

    // Initialize debounced functions
    this.#debouncedSave = this.#debounce((value) => {
      localStorage.setItem(this.#storageKey, value)
    }, 100)

    this.#debouncedOnWindowResize = this.#debounce(() => {
      this.#onWindowResize()
    }, 100)

    // Only apply width adjustments and attach event listeners on non-mobile devices
    if (!this.#isMobileDevice()) {
      // Restore saved width or use current width
      const savedWidth = localStorage.getItem(this.#storageKey)
      const initialWidth = savedWidth ? parseInt(savedWidth, 10) : this.leftTarget.offsetWidth
      this.#applyWidth(this.#getBoundedWidth(initialWidth))

      this.handleTarget.addEventListener("mousedown", this.#startResize)
      window.addEventListener("resize", this.#debouncedOnWindowResize)
    } else {
      // On mobile, ensure no inline width styles are set (let CSS handle it)
      this.leftTarget.style.width = ""
      this.handleTarget.style.left = ""
    }
  }

  disconnect() {
    if (!this.#isMobileDevice()) {
      this.handleTarget.removeEventListener("mousedown", this.#startResize)
      window.removeEventListener("resize", this.#debouncedOnWindowResize)
    }
    // Clean up any active drag listeners
    this.#stopResize()
  }

  #startResize = (event) => {
    event.preventDefault()
    this.#isDragging = true
    const rect = this.leftTarget.getBoundingClientRect()
    this.#startX = event.clientX
    this.#startWidth = rect.width

    // Apply drag styles
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
    this.handleTarget.classList.add("dragging")

    // Attach drag listeners only when dragging starts
    document.addEventListener("mousemove", this.#resize)
    document.addEventListener("mouseup", this.#stopResize)
  }

  #resize = (event) => {
    const dx = event.clientX - this.#startX
    const newWidth = this.#startWidth + dx
    const bounded = this.#getBoundedWidth(newWidth)
    this.#applyWidth(bounded)
    this.#debouncedSave(bounded)
  }

  #stopResize = () => {
    if (!this.#isDragging) return

    this.#isDragging = false

    // Remove drag listeners
    document.removeEventListener("mousemove", this.#resize)
    document.removeEventListener("mouseup", this.#stopResize)

    // Restore styles
    document.body.style.cursor = ""
    document.body.style.userSelect = ""
    this.handleTarget.classList.remove("dragging")
  }

  // Apply width and update handle position in one operation
  #applyWidth(width) {
    this.leftTarget.style.width = `${width}px`
    // Handle sits in the middle of the reserved margin-right area
    const position = width + this.#leftMarginRight / 2 - this.#handleWidth / 2
    this.handleTarget.style.left = `${position}px`
  }

  #getBoundedWidth(value) {
    const mainWidth = this.#mainElement?.offsetWidth || window.innerWidth

    // min: 50% main-content
    const minByMain = mainWidth * 0.5

    // max: mainWidth - minRightWidth (ensures right panel has minimum space)
    const maxByRight = mainWidth - this.#minRightWidth

    return Math.max(minByMain, Math.min(maxByRight, value))
  }

  #onWindowResize() {
    // Only handle resize on non-mobile devices
    if (this.#isMobileDevice()) {
      // Clear inline styles on mobile to let CSS handle the layout
      this.leftTarget.style.width = ""
      this.handleTarget.style.left = ""
      return
    }

    const current = this.leftTarget.offsetWidth
    const bounded = this.#getBoundedWidth(current)
    this.#applyWidth(bounded)
  }

  #parseRemToPx(value) {
    if (value.endsWith("rem")) {
      const rem = parseFloat(value)
      return rem * parseFloat(getComputedStyle(document.documentElement).fontSize)
    }
    return parseInt(value, 10)
  }

  #getStorageKey() {
    const baseKey = this.element.dataset.splitId || this.element.id || this.#indexKey()
    return `split-left-width-${baseKey}`
  }

  #indexKey() {
    const allSplits = document.querySelectorAll("[data-controller~='split-panel']")
    return Array.from(allSplits).indexOf(this.element)
  }

  #debounce(fn, delay = 100) {
    let timer
    return (...args) => {
      clearTimeout(timer)
      timer = setTimeout(() => fn(...args), delay)
    }
  }

  #isMobileDevice() {
    return window.innerWidth <= 992
  }

  // Private properties
  #isDragging = false
  #startX = 0
  #startWidth = 0
  #handleWidth = 0
  #minRightWidth = 0
  #leftMarginRight = 0
  #mainElement = null
  #storageKey = ""
  #debouncedSave = null
  #debouncedOnWindowResize = null
}
